<template>
    <v-row>
        <v-expansion-panels
                v-model="panelIndex"
                accordion
                @change="activateLayer"
        >
            <v-expansion-panel
                    v-for="wmsLayer in filteredWmsLayerList"
                    :key="wmsLayer.name"
                    class="layer-panel"
            >
                <v-expansion-panel-header>
                    <v-checkbox
                            v-model="wmsLayer.active"
                            :label="wmsLayer.title"
                            class="menu-checkbox"
                            @click="wmsLayer.active = !wmsLayer.active"
                    />
                </v-expansion-panel-header>
                <v-expansion-panel-content style="padding: 0 10px;">
                    <v-select
                            v-if="wmsLayer.timeSettings"
                            v-model="wmsLayer.selectedTime"
                            :items="wmsLayer.times"
                            label="Datetime"
                            item-text="date"
                            item-value="values"
                            :loading="wmsLayer.loading"
                            return-object
                            @change="changeTime(wmsLayer)"
                            prepend-icon="fas fa-caret-left"
                            append-outer-icon="fas fa-caret-right"
                            @click:append-outer="nextTime(wmsLayer)"
                            @click:prepend="prevTime(wmsLayer)"
                    />
                    <v-select
                            v-if="wmsLayer.styleSettings"
                            v-model="wmsLayer.selectedStyle"
                            :items="wmsLayer.styles"
                            item-text="Title"
                            item-value="Name"
                            label="Style"
                            :loading="wmsLayer.loading"
                            return-object
                            @change="changeStyle(wmsLayer)"
                    />
                    <v-slider
                            v-model="wmsLayer.opacity"
                            label="Transparenz"
                            min="0"
                            max="100"
                            class="menu-slider"
                            @change="changeOpacity(wmsLayer)"
                    >
                        <template v-slot:append>
                            <v-icon @click="setOpacityFull(wmsLayer)">
                                fa fa-eye
                            </v-icon>
                        </template>
                    </v-slider>
                    <v-row
                            justify="space-around"
                            no-gutters
                    >
                        <v-btn
                                text
                                @click="openLayerInfoDialog(wmsLayer)"
                        >
                            Weitere Informationen
                        </v-btn>
                    </v-row>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
        <v-dialog
                v-model="layerInfoDialog.open"
                max-width="800"
        >
            <v-card>
                <v-card-title
                        class="headline primary lighten-1 white--text"
                >
                    {{ layerInfoDialog.title }}
                </v-card-title>
                <v-card-text class="mt-5">
                    {{ layerInfoDialog.content }}
                </v-card-text>
                <v-divider/>

                <v-card-actions>
                    <v-spacer/>
                    <v-btn
                            color="primary"
                            text
                            @click="layerInfoDialog.open = false"
                    >
                        Ok
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
    import {mapState} from "vuex"
    import WmsCapabilities from "@/services/wms.service";
    import LeafletServices from "@/services/leaflet.service";

    export default {
        name: "Menu",
        props: ["layerCategory"],
        data() {
            return {
                panelIndex: null,
                wmsLayerList: null,
                layerInfoDialog: {
                    open: false,
                    title: null,
                    content: null
                }
            }
        },
        computed: {
            ...mapState("map", ["layerSettings"]),
            filteredWmsLayerList: function () {
                return this.wmsLayerList.filter(layer => layer.category === this.layerCategory)
            }
        },
        created: function () {
            this.wmsLayerList = WmsCapabilities.createWmsLayerList();
            this.layerSettings.forEach(service => {
                this.addCapabilitiesPerService(service)
            })
        },
        methods: {
            activateLayer: function () {
                this.wmsLayerList.forEach(layer => {
                    layer.active = false
                });

                LeafletServices.removeAllLayers();

                if (this.panelIndex != null) {
                    this.filteredWmsLayerList[this.panelIndex].active = true;
                    LeafletServices.addLayerToMap(this.filteredWmsLayerList[this.panelIndex].layer);
                    this.$emit("selectedLayer", this.filteredWmsLayerList[this.panelIndex])
                } else {
                    this.$emit("selectedLayer", null)
                }
            },

            changeOpacity: function (wmsLayer) {
                wmsLayer.layer.setOpacity(wmsLayer.opacity / 100)
            },

            changeStyle: function (wmsLayer) {
                wmsLayer.layer.setParams({styles: wmsLayer.selectedStyle.Name})
            },

            nextItem: function (arr, i) {
                i += 1; // increase i by one
                i = i % arr.length; // if we've gone too high, start from `0` again
                return arr[i]; // give us back the item of where we are now
            },

            prevItem: function (arr, i) {
                if (i === 0) { // i would become 0
                    i = arr.length; // so put it at the other end of the array
                }
                i -= 1; // decrease by one
                return arr[i]; // give us back the item of where we are now
            },

            changeTime: function (wmsLayer) {
                wmsLayer.layer.setParams({time: wmsLayer.selectedTime.values})
            },

            nextTime: function (wmsLayer) {
                let i = wmsLayer.times.indexOf(wmsLayer.selectedTime);
                let next = this.nextItem(wmsLayer.times, i);

                wmsLayer.selectedTime = next;
                wmsLayer.layer.setParams({time: next.values})
            },

            prevTime: function (wmsLayer) {
                let i = wmsLayer.times.indexOf(wmsLayer.selectedTime);
                let prev = this.prevItem(wmsLayer.times, i);

                wmsLayer.selectedTime = prev;
                wmsLayer.layer.setParams({time: prev.values})
            },

            setStyle: function (wmsCapabilities, layer) {
                // Always get styles for handling legend image
                layer.styles = WmsCapabilities.getStyles(wmsCapabilities, layer);
                // Set default value
                layer.selectedStyle = layer.styles[0];
                layer.layer.setParams({styles: layer.selectedStyle.Name});

                return layer
            },

            setTime: function (wmsCapabilities, layer) {
                layer.times = WmsCapabilities.getTime(wmsCapabilities, layer.name, layer.timeSettings);
                // Set default value
                layer.selectedTime = layer.times[0];
                layer.layer.setParams({time: layer.selectedTime.values});

                return layer
            },

            addCapabilitiesPerService: async function (service) {
                const wmsCapabilities = await WmsCapabilities.getJson(service.baseUrl);
                const serviceLayer = this.wmsLayerList.filter(layer => {
                    return layer.service === service.service
                })

                serviceLayer.forEach(layer => {
                    layer = this.setStyle(wmsCapabilities, layer);

                    if (layer.timeSettings) {
                        layer = this.setTime(wmsCapabilities, layer)
                    }

                    if (layer.description == null) {
                        layer.description = WmsCapabilities.getDescription(wmsCapabilities, layer)
                    }

                    layer.loading = false
                })
            },

            setOpacityFull: function (wmsLayer) {
                wmsLayer.opacity = 100;
                wmsLayer.layer.setOpacity(1)
            },

            openLayerInfoDialog: function (wmsLayer) {
                this.layerInfoDialog.title = wmsLayer.title;
                this.layerInfoDialog.content = wmsLayer.description;
                this.layerInfoDialog.open = true
            }
        }
    }
</script>

<style scoped>
    .menu-checkbox {
        height: 30px;
        padding: 0;
        margin: auto 0;
    }

    .menu-slider {
        padding-top: 12px;
        margin-top: 4px;
    }
</style>